import {
  Badge,
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../../../global/theme/tokens';
import { useState } from 'react';
import { API_PATCH, DBRequest, useConfig } from '../../../../api/api';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WPEToDoElementPopup from './WPEToDoElementPopup';
import { ProjectUserStack } from '../ProjectUserStack';
import { Draggable } from 'react-beautiful-dnd';
import { createPortal } from 'react-dom';
import RenderHTML from '../../../preview/RenderHTML';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { TimeLabel } from '../../../label/TimeLabel';
import DateLabel from '../../../label/DateLabel';
import { checkDeadlinePassed } from '../../../special/checkDeadlinePassed';
import MediaPreview from '../../explorer/MediaPreview';
import { Link } from 'react-router-dom';

export const WPEToDoElement = ({
  element,
  onChange,
  onDelete,
  className,
  index,
  wpeId,
  pid,
  disableDragging,
  deleteMode,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const config = useConfig();

  const [isProcessing, setIsProcessing] = useState(false);
  const [dataCollection, setDataCollection] = useState(element);
  const [openPopup, setOpenPopup] = useState(false);

  function handleToggleComplete() {
    if (isProcessing) return;
    element.status ? (dataCollection.status = 0) : (dataCollection.status = 1);
    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${element.id}`,
      method: API_PATCH(dataCollection),
      onResponse: handleResponse,
      onLoading: setIsProcessing,
    });
  }

  function handleStatusChange() {
    const newPriority = (element.priority + 1) % 4;
    dataCollection.priority = newPriority;
    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${element.id}`,
      method: API_PATCH(dataCollection),
      onResponse: handleResponse,
      //onLoading: setIsProcessing,
    });
  }

  function handleResponse(res) {
    onChange(res.data);
    setDataCollection(res.data);
    element = res.data;
  }

  const priorityColorMap = {
    0: colors.grey,
    1: colors.success,
    2: colors.warning,
    3: colors.error,
  };

  return (
    <Draggable
      isDragDisabled={disableDragging}
      key={`${element.id}`}
      draggableId={`${element.id}`}
      index={index}
      //sx={{ position: "fixed" }}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className=" cursor-pointer"
        >
          {!snapshot.isDragging && (
            <TaskContent
              element={element}
              onDelete={onDelete}
              className={className}
              priorityColorMap={priorityColorMap}
              onToggleComplete={handleToggleComplete}
              isProcessing={isProcessing}
              openPopup={openPopup}
              setOpenPopup={setOpenPopup}
              handleResponse={handleResponse}
              handleStatusChange={handleStatusChange}
              wpeId={wpeId}
              pid={pid}
              deleteMode={deleteMode}
            />
          )}

          {snapshot.isDragging &&
            createPortal(
              <div
                style={{
                  ...provided.draggableProps.style,
                  touchAction: 'none',
                  zIndex: 9999,
                }}
              >
                <TaskContent
                  element={element}
                  onDelete={onDelete}
                  className={className}
                  priorityColorMap={priorityColorMap}
                  onToggleComplete={handleToggleComplete}
                  isProcessing={isProcessing}
                  openPopup={openPopup}
                  setOpenPopup={setOpenPopup}
                  handleResponse={handleResponse}
                  handleStatusChange={handleStatusChange}
                  wpeId={wpeId}
                  pid={pid}
                />
              </div>,
              document.body // Render outside the drag container to prevent layout issues
            )}
        </div>
      )}
    </Draggable>
  );
};

export const TaskContent = ({
  element,
  onDelete,
  className,
  priorityColorMap,
  onToggleComplete,
  isProcessing,
  setOpenPopup,
  openPopup,
  handleResponse,
  handleStatusChange,
  wpeId,
  pid,
  deleteMode,
  innerClassName,
  hideUsers,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const [openDiscription, setOpenDiscription] = useState(false);

  // Reminder Passed Tasks
  const deadlinePassed =
    element.deadline && checkDeadlinePassed(element.deadline);

  const { t } = useTranslation();

  function handleDelete(e) {
    e.stopPropagation();
    onDelete(element);
  }

  return (
    <Box className={`flex relative my-[3px] ${className}`}>
      <Badge
        badgeContent={'X'}
        onClick={handleDelete}
        invisible={!deleteMode}
        color="error"
        sx={{
          position: 'absolute',
          top: 0,
          right: 5,
          cursor: 'pointer', // Only applies to the badge
          '& .MuiBadge-badge': {
            pointerEvents: 'auto', // Ensure badge receives clicks
          },
        }}
      />
      <Box
        className="w-[10px] h-full absolute rounded-l-lg"
        onClick={handleStatusChange}
        sx={{
          background: priorityColorMap[element.priority],
          pointerEvents: 'auto',
        }}
      />
      <Box
        className={`flex items-center gap-2 rounded-lg px-3 py-1 w-full `}
        sx={{
          backgroundColor: colors.bgInput,
          border: '1px solid transparent',
        }}
      >
        <Box>
          <IconButton onClick={onToggleComplete}>
            {isProcessing ? (
              <span
                className="loader "
                style={{ width: '20px', height: '20px' }}
              />
            ) : element.status ? (
              <TaskAltIcon />
            ) : (
              <RadioButtonUncheckedIcon />
            )}
          </IconButton>
        </Box>
        <Box
          className={'w-full h-full items-center flex' + innerClassName}
          onDoubleClick={() => setOpenPopup(true)}
        >
          <Typography
            className="w-full cursor-pointer"
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              fontSize: '14px',
              color: deadlinePassed ? colors.error : 'inherit', // set red color if deadline has passed
            }}
            variant="h5"
            fontWeight={500}
          >
            {element.name}
          </Typography>
          {(element.description || element.file) && (
            <Box className="flex flex-col gap-1">
              <Button
                sx={{ padding: 0, justifyContent: 'space-between' }}
                onClick={() => setOpenDiscription(!openDiscription)}
                className="flex items-center justify-between"
              >
                <Typography
                  fontWeight={500}
                  fontSize="12px"
                  className="opacity-60"
                >
                  {openDiscription ? t('show less...') : t('show more...')}
                </Typography>
                <ArrowDropDownIcon
                  sx={{
                    transform: `rotate(${openDiscription ? 180 : 0}deg)`,
                  }}
                />
              </Button>
              <Collapse in={openDiscription} unmountOnExit>
                <RenderHTML HTML={element.description} emptyHTML={' '} />
                {element.file && (
                  <Link
                    href={element.file.temporaryUrl}
                    className="flex w-full"
                  >
                    <MediaPreview
                      key={element.file.id}
                      src={
                        element.file.temporaryUrl ?? element.file.temporaryUrl
                      }
                      size="50px"
                      onlyIcons={false}
                      allowPreview="true"
                    />
                  </Link>
                )}
              </Collapse>
            </Box>
          )}
          {element.deadline && (
            <Typography
              fontSize="11px"
              className="opacity-50 w-full"
              textAlign="right"
            >
              <DateLabel date={element.deadline} />
            </Typography>
          )}
          {!hideUsers && element?.users?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <ProjectUserStack
                editmode={false}
                users={element.users}
                noLabel={true}
              />
            </Box>
          )}
        </Box>
        <WPEToDoElementPopup
          element={element}
          open={openPopup}
          onClose={() => setOpenPopup(false)}
          onResponse={handleResponse}
          onDelete={onDelete}
          wpeId={wpeId}
          pid={pid}
        />
      </Box>
    </Box>
  );
};
